// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ConfigHandler from '../../loaders/handlers/ConfigHandler';
import AnimationLayer from '../../animationpack/layer/AnimationLayer';
import ObjectUtils from '../../util/ObjectUtils';

/**
 * Handler for loading animation layers
 */
class AnimationLayersHandler extends ConfigHandler {

  /**
   * @param {...*} args {@link ConfigHandler} constructor arguments.
   */
  constructor(...args) {
    super(...args);
  }

  /**
   * Creates an empty array to store animation layers
   * @param {string} ref
   * @returns {Array<AnimationLayer>}
   * @private
   */
  _create(ref) {
    const layer = [];
    this._objects.set(ref, layer);
    return layer;
  }

  /**
   * Sets current state on a layer if possible, otherwise clears  current state
   * @param {AnimationLayer} layer
   * @param {string} stateKey
   */
  _setInitialState(layer, stateKey) {
    if (stateKey) {
      const state = layer.getStateById(stateKey);
      if (layer._currentState !== state) {
        layer.setCurrentStateById(stateKey, true);
      }
    } else {
      layer.setCurrentState();
    }
  }

  /**
   * Adds/updates/removes the animation layers
   * @param {string} ref
   * @param {Object} config
   * @param {Object} options
   * @returns {Promise} Resolves with the updated animation state or null if removed
   */
  _update(ref, config, options) {
    const that = this;
    return ConfigHandler.prototype._update.call(this, ref, config, options).then((object) => {
      if (!object) { return; }
      const promises = [];

      let i = 0;
      ObjectUtils.forEach(config.layers, (layerCfg) => {
        promises.push(that._parseLayer(layerCfg, object[i++], options));
      }, null, 'sortValue');

      return Promise.all(promises).then((layers) => {
        object.length = layers.length;
        for (let i = 0; i < layers.length; i++) {
          object[i] = layers[i];
        }
        return object;
      });
    });
  }

  /**
   * Parses a single layer, puts the correct properties and {@link SteadyState} onto it
   * @param {Object} layerConfig
   * @param {AnimationLayer} layer
   * @param {Object} [options]
   * @returns {Promise} resolves with layer
   * @private
   */
  _parseLayer(layerConfig, layer, options) {
    const that = this;

    if (!layer) {
      layer = new AnimationLayer(layerConfig.name, undefined, this.world);
    } else {
      layer._name = layerConfig.name;
    }

    layer.id = layerConfig.id;
    layer._transitions = ObjectUtils.deepClone(layerConfig.transitions);

    if (layer._layerBlender) {
      if (layerConfig.blendWeight !== undefined) {
        layer._layerBlender._blendWeight = layerConfig.blendWeight;
      } else {
        layer._layerBlender._blendWeight = 1.0;
      }

      layer._layerType = layerConfig.layerType || AnimationLayer.DEFAULT_LAYER_TYPE;
    }

    // Load all the stuff we need
    const promises = [];
    ObjectUtils.forEach(layerConfig.states, (stateCfg) => {
      promises.push(that.loadObject(stateCfg.stateRef, options).then((state) => {
        layer.setState(state.id, state);
      }));
    }, null, 'sortValue');

    // Populate layer
    return Promise.all(promises).then(() => {
      that._setInitialState(layer, layerConfig.initialStateRef);
      return layer;
    });
  }
}

ConfigHandler._registerClass('animation', AnimationLayersHandler);

export default AnimationLayersHandler;
